/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToRouter from 'js/lib/connectToRouter';

import type { Theme } from '@coursera/cds-core';
import { Grid, Link, Typography, Typography2, withTheme } from '@coursera/cds-core';

import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import ShareButtonWithModal from 'bundles/sharing-common/components/modal/ShareButtonWithModal';
import ShareCTAV2 from 'bundles/sharing-common/components/modal/buttons/cds/ShareCTAV2';
import SharePreviewCard from 'bundles/video-logged-out-page/components/SharePreviewCard';
import { SESSION_STORAGE_AUTO_PLAY_VIDEO_ITEM } from 'bundles/video-logged-out-page/constants';
import type {
  SessionStorageAutoplayItem,
  VLPItem,
  VideoPlayer,
} from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import getSocialCaptions from 'bundles/video-logged-out-page/utils/getSocialCaptions';

import _t from 'i18n!nls/video-logged-out-page';

const styles = {
  nextVideoCounterWrapper: css`
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    padding-left: 10%;
    padding-right: 10%;
  `,
  videoOverlay: css`
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: radial-gradient(circle, rgb(8 39 78 / 70%) 0%, var(--cds-color-blue-950) 100%);
  `,
  nextVideoTextWrapper: css`
    position: absolute;
    top: 40%;
    width: 85%;
    font-size: 18px;

    @media (max-width: 767px) {
      top: 20%;
    }
  `,
  nextVideoName: (theme: Theme) => css`
    font-size: 22px;
    color: var(--cds-color-neutral-primary-invert);
    padding-bottom: 20px;
  `,
  nextVideoPauseButton: css`
    position: relative;
    top: 5px;
    left: 24px;
    background-color: transparent;
    border: 0;
    padding: 0;
  `,
  nextVideoShare: css`
    margin-top: 20px;

    @media (max-width: 767px) {
      display: none;
    }
  `,
  nextVideoTitle: (theme: Theme) => css`
    text-transform: uppercase;
    color: var(--cds-color-neutral-primary-invert);
  `,
  nextVideoPauseIcon: (theme: Theme) => css`
    height: 22px;
    border-right: 8px solid var(--cds-color-white-0);
    border-left: 8px solid var(--cds-color-white-0);
    width: 22px;
  `,

  nextVideoText: (theme: Theme) => css`
    color: var(--cds-color-neutral-primary-invert);
    margin-right: 5px;
  `,
  counterContainer: css`
    display: flex;
    align-items: baseline;
  `,
};

const COUNTER_START = 9;

type Props = {
  nextItemObj: VLPItem;
  courseSlug: string;
  videoPlayer: VideoPlayer | null;
  currentItemTitle: string;
  courseName?: string;
  partnerNames?: string | null;
  screenshot?: string;
  getVideoTime: () => number;
};

type PropsFromCDS = {
  theme: Theme;
};

type PropsToComponent = Props & PropsFromCDS;

type State = {
  counter: number;
  showCounter: boolean;
  componentDidMount: boolean;
};

export class NextVideoCounter extends React.Component<PropsToComponent, State> {
  declare intervalId: number | undefined;

  declare nextVideoUrl: string;

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof NextVideoCounter.contextTypes>;

  state = {
    counter: COUNTER_START,
    showCounter: true,
    componentDidMount: false,
  };

  constructor(props: PropsToComponent) {
    super(props);
    const { courseSlug, nextItemObj } = props;

    this.nextVideoUrl = `/lecture/${courseSlug}/${nextItemObj.slug}-${nextItemObj.id}`;
  }

  componentDidMount() {
    const { videoPlayer } = this.props;
    this.setState(() => ({ componentDidMount: true }));
    if (videoPlayer?.isFullscreen()) videoPlayer.exitFullscreen();
    this.intervalId = window.setInterval(() => {
      this.setState(({ counter }) => ({ counter: counter - 1 }));
    }, 1000);
  }

  componentDidUpdate(prevProps: $TSFixMe, prevState: $TSFixMe) {
    const { router } = this.context;
    const { counter, showCounter, componentDidMount } = prevState;
    if (counter === 1 && showCounter) {
      this.stopCounter(() => {
        if (componentDidMount && sessionStorageEx.isAvailable()) {
          // store session storage item to tell the next page to auto play the next video
          // doing this so we can easily remove this item (in vlpQueriesHOC) on next page state, if a full page refresh happens with auto play active it causes an error
          sessionStorageEx.setItem<SessionStorageAutoplayItem>(
            SESSION_STORAGE_AUTO_PLAY_VIDEO_ITEM,
            { autoPlayVideo: true },
            JSON.stringify
          );
        }
        router.replace({
          pathname: this.nextVideoUrl,
        });
      });
    }
  }

  stopCounterOnClick = () => this.stopCounter();

  stopCounter = (callBackFn?: () => void) => {
    window.clearInterval(this.intervalId);
    this.setState(
      () => ({ showCounter: false }),
      () => {
        if (callBackFn && typeof callBackFn === 'function') {
          callBackFn();
        }
      }
    );
  };

  render() {
    const { nextItemObj, currentItemTitle, partnerNames, courseName, screenshot, getVideoTime, theme } = this.props;
    const { counter, showCounter } = this.state;

    return (
      <div css={styles.nextVideoCounterWrapper}>
        <div css={styles.videoOverlay} />
        <div css={styles.nextVideoTextWrapper}>
          <Typography2 component="p" variant="bodyPrimary" css={styles.nextVideoTitle(theme)}>
            {_t('Next up:')}
          </Typography2>
          <Link
            component={TrackedA}
            variant="quiet"
            css={styles.nextVideoName(theme)}
            href={this.nextVideoUrl}
            trackingName="vlp_next_video_link"
          >
            {nextItemObj.name}
          </Link>
          {showCounter && (
            <Grid css={styles.counterContainer}>
              <Typography css={styles.nextVideoText(theme)} variant="h2">
                {_t('Starting in: ')}
              </Typography>
              <Typography css={styles.nextVideoText(theme)} variant="h2">
                {counter}
              </Typography>
              <TrackedButton
                css={styles.nextVideoPauseButton}
                type="button"
                aria-live="polite"
                onClick={this.stopCounterOnClick}
                trackingName="click_pause_video_button"
              >
                <div css={styles.nextVideoPauseIcon(theme)} />
              </TrackedButton>
            </Grid>
          )}
          <TrackedDiv
            css={styles.nextVideoShare}
            trackingName="universal_sharing_cta_next_video"
            onClick={this.stopCounterOnClick}
          >
            <ShareButtonWithModal
              tabbable={true}
              utmMediumParam="page_share"
              utmContentParam="vlp"
              utmCampaignParam="next_video_button"
              title={_t('Share this video')}
              description={_t('If you enjoy this video, take the time to show your friends.')}
              previewComponent={
                <SharePreviewCard title={currentItemTitle} partnerName={partnerNames} imageSrc={screenshot} />
              }
              captions={getSocialCaptions({
                videoTitle: currentItemTitle,
                courseName,
                partnerName: partnerNames,
              })}
              getVideoTimeInSeconds={getVideoTime}
            >
              <ShareCTAV2 isDark />
            </ShareButtonWithModal>
          </TrackedDiv>
        </div>
      </div>
    );
  }
}
const NextVideoCounterWithTheme = withTheme(NextVideoCounter);
export default connectToRouter<Props, Omit<Props, 'courseSlug'>>(({ params: { courseSlug } }) => ({
  courseSlug,
}))(NextVideoCounterWithTheme);
